import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  ChevronDown,
  ArrowLeft,
  Download,
  Bell,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import Reveal from "@/components/site/Reveal";
import PhoneMockup from "@/components/site/PhoneMockup";
import FoxMotif from "@/components/site/FoxMotif";
import AppStoreBadge from "@/components/site/AppStoreBadge";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import NotifyForm from "@/components/site/NotifyForm";
import { useAppPages } from "@/contexts/AppPagesContext";
import { useContent } from "@/contexts/ContentContext";
import { getIcon } from "@/lib/icons";
import { mediaUrl } from "@/lib/api";

const FallbackHero = () => (
  <div
    className="absolute inset-0 bg-gradient-to-br from-[#FFEEE6] via-[#FBFAF8] to-[#FFE2D2] flex items-center justify-center"
    aria-hidden="true"
  >
    <FoxMotif size={120} testId="apppage-hero-fox" />
  </div>
);

export default function AppPageView() {
  const { slug } = useParams();
  const { pages, loading, findBySlug } = useAppPages();
  const { content } = useContent();
  const reduce = useReducedMotion();
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifySession, setNotifySession] = useState(0);

  const page = findBySlug(slug);

  const openNotify = () => {
    setNotifySession((s) => s + 1);
    setNotifyOpen(true);
  };

  if (loading && pages.length === 0) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Nav />
        <div className="pf-container py-32 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#F35A1F]" />
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">
        <Nav />
        <main
          className="pf-container py-32 text-center"
          data-testid="app-page-not-found"
        >
          <p className="pf-mono text-[11px] uppercase tracking-wider text-[#F35A1F]">
            404 · Not found
          </p>
          <h1 className="mt-3 pf-display text-4xl">
            We couldn&apos;t find that app.
          </h1>
          <p className="mt-3 text-[hsl(var(--muted-foreground))]">
            It might have moved, or been retired into the den.
          </p>
          <Link
            to="/"
            data-testid="app-page-not-found-home"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#F35A1F] px-5 py-3 text-sm font-semibold text-white hover:bg-[#D94A14]"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Play Fox Studio
          </Link>
        </main>
        <Footer content={content?.footer} />
      </div>
    );
  }

  const sections = page.sections || {};
  const accent = page.accent_color || "#F35A1F";
  const hasAppStoreLink = page.app_store_url && page.app_store_url !== "#" && page.app_store_url.length > 1;

  return (
    <div
      className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))]"
      data-testid={`app-page-${page.slug}`}
    >
      <Nav />

      {/* Hero */}
      {sections.hero !== false && (
        <section
          data-testid={`app-page-${page.slug}-hero`}
          className="relative overflow-hidden pt-10 sm:pt-14 lg:pt-20 pb-16"
        >
          <div
            className="pointer-events-none absolute -top-32 -left-32 h-[520px] w-[520px] pf-hero-blob"
            aria-hidden="true"
          />
          <div
            className="pointer-events-none absolute inset-0 pf-grid-bg opacity-[0.3]"
            aria-hidden="true"
          />
          <div
            className="pointer-events-none absolute inset-0 pf-noise"
            aria-hidden="true"
          />

          <div className="pf-container relative">
            <Link
              to="/"
              data-testid={`app-page-${page.slug}-back`}
              className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-6"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to all apps
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-6">
                <Reveal>
                  <div className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-white pl-1 pr-3 py-1 pf-shadow-sm">
                    <span
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full"
                      style={{ backgroundColor: accent }}
                    >
                      <Sparkles className="h-3.5 w-3.5 text-white" />
                    </span>
                    <span
                      className="pf-mono text-[11px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]"
                      data-testid={`app-page-${page.slug}-hero-eyebrow`}
                    >
                      {page.hero_eyebrow ||
                        (page.coming_soon ? "Coming Soon" : "Now Available")}
                    </span>
                  </div>
                </Reveal>

                <Reveal delay={0.06}>
                  <h1
                    data-testid={`app-page-${page.slug}-hero-headline`}
                    className="mt-5 pf-display text-[42px] leading-[1.04] sm:text-[58px] lg:text-[72px] tracking-tight"
                  >
                    {page.hero_headline_line1 || page.title}
                    {page.hero_headline_line2 ? (
                      <>
                        <br />
                        <span
                          className="relative inline-block"
                          style={{ color: accent }}
                        >
                          <span className="relative z-10">
                            {page.hero_headline_line2}
                          </span>
                          {!reduce && (
                            <motion.span
                              aria-hidden
                              initial={{ scaleX: 0 }}
                              animate={{ scaleX: 1 }}
                              transition={{
                                delay: 0.5,
                                duration: 0.6,
                                ease: [0.2, 0.8, 0.2, 1],
                              }}
                              style={{
                                originX: 0,
                                backgroundColor: `${accent}26`,
                              }}
                              className="absolute -bottom-1 left-0 right-0 h-2 rounded-full"
                            />
                          )}
                        </span>
                      </>
                    ) : null}
                  </h1>
                </Reveal>

                {(page.hero_subheadline || page.tagline) && (
                  <Reveal delay={0.12}>
                    <p
                      data-testid={`app-page-${page.slug}-hero-sub`}
                      className="mt-5 max-w-xl text-base sm:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed"
                    >
                      {page.hero_subheadline || page.tagline}
                    </p>
                  </Reveal>
                )}

                <Reveal delay={0.18}>
                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    {page.coming_soon || !hasAppStoreLink ? (
                      <Button
                        data-testid={`app-page-${page.slug}-notify-cta`}
                        onClick={openNotify}
                        className="group h-12 rounded-xl px-5 text-base text-white focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2"
                        style={{ backgroundColor: accent }}
                      >
                        <Bell className="h-4 w-4 mr-2" />
                        Notify me at launch
                      </Button>
                    ) : (
                      <a
                        href={page.app_store_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`app-page-${page.slug}-appstore-cta`}
                      >
                        <AppStoreBadge />
                      </a>
                    )}
                    {page.learn_more_url ? (
                      <a
                        href={page.learn_more_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`app-page-${page.slug}-learn-more`}
                        className="inline-flex items-center gap-1.5 h-12 rounded-xl border border-[hsl(var(--border))] bg-white px-5 text-base text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]"
                      >
                        Learn more
                        <ArrowRight className="h-4 w-4" />
                      </a>
                    ) : null}
                  </div>
                </Reveal>

                {page.coming_soon ? (
                  <Reveal delay={0.24}>
                    <div className="mt-6 inline-flex items-center gap-2">
                      <Badge
                        data-testid={`app-page-${page.slug}-coming-soon-badge`}
                        className="rounded-full bg-[#FFEEE6] text-[#D94A14] hover:bg-[#FFEEE6] border-0 px-2.5 py-0.5 text-[11px] font-semibold"
                      >
                        COMING SOON
                      </Badge>
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">
                        Get early access — join the launch list.
                      </span>
                    </div>
                  </Reveal>
                ) : null}
              </div>

              <div
                className="lg:col-span-6 relative"
                data-testid={`app-page-${page.slug}-hero-phone-wrap`}
              >
                <div className="relative flex justify-center lg:justify-end">
                  <PhoneMockup
                    variant="featured"
                    imageId={page.hero_image_id}
                    testId={`app-page-${page.slug}-hero-phone`}
                  />
                  {!page.hero_image_id && (
                    <div className="hidden">
                      <FallbackHero />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Screenshots */}
      {sections.screenshots !== false &&
        Array.isArray(page.screenshots) &&
        page.screenshots.length > 0 && (
          <section
            data-testid={`app-page-${page.slug}-screenshots`}
            className="pf-section bg-[hsl(var(--accent))]/40 border-y border-[hsl(var(--border))]"
          >
            <div className="pf-container">
              <Reveal>
                <div className="flex items-center gap-2">
                  <span
                    className="pf-mono text-[11px] uppercase tracking-wider"
                    style={{ color: accent }}
                  >
                    {page.screenshots_eyebrow || "Screenshots"}
                  </span>
                  <span className="h-px w-10 bg-[hsl(var(--border))]" />
                </div>
                <h2 className="mt-3 pf-display text-[28px] sm:text-[36px] leading-tight tracking-tight">
                  {page.screenshots_title || "Take a peek inside."}
                </h2>
              </Reveal>

              <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {page.screenshots.map((shot, i) => {
                  const url = shot.image_id ? mediaUrl(shot.image_id) : null;
                  return (
                    <Reveal key={shot.id || i} delay={0.04 * i}>
                      <figure
                        data-testid={`app-page-${page.slug}-screenshot-${i}`}
                        className="relative overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-white pf-shadow-sm"
                        style={{ aspectRatio: "9/19.5" }}
                      >
                        {url ? (
                          <img
                            src={url}
                            alt={shot.caption || `${page.title} screenshot ${i + 1}`}
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-[hsl(var(--muted-foreground))] text-xs">
                            (No image)
                          </div>
                        )}
                        {shot.caption ? (
                          <figcaption className="absolute bottom-0 inset-x-0 bg-[#121316]/85 text-white text-[11px] py-1.5 px-3 backdrop-blur-sm">
                            {shot.caption}
                          </figcaption>
                        ) : null}
                      </figure>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </section>
        )}

      {/* Features */}
      {sections.features !== false &&
        Array.isArray(page.features) &&
        page.features.length > 0 && (
          <section
            data-testid={`app-page-${page.slug}-features`}
            className="pf-section"
          >
            <div className="pf-container">
              <Reveal>
                <div className="flex items-center gap-2">
                  <span
                    className="pf-mono text-[11px] uppercase tracking-wider"
                    style={{ color: accent }}
                  >
                    {page.features_eyebrow || "Features"}
                  </span>
                  <span className="h-px w-10 bg-[hsl(var(--border))]" />
                </div>
                <h2 className="mt-3 pf-display text-[32px] sm:text-[44px] leading-tight tracking-tight max-w-2xl">
                  {page.features_title || "Small touches, big delight."}
                </h2>
              </Reveal>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {page.features.map((feat, i) => {
                  const Icon = getIcon(feat.icon || "Sparkles");
                  return (
                    <Reveal key={feat.id || i} delay={0.04 * i}>
                      <article
                        data-testid={`app-page-${page.slug}-feature-${i}`}
                        className="group h-full rounded-2xl border border-[hsl(var(--border))] bg-white p-6 pf-shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5"
                      >
                        <span
                          className="inline-flex h-11 w-11 items-center justify-center rounded-xl"
                          style={{ backgroundColor: "#FFEEE6", color: "#D94A14" }}
                        >
                          <Icon className="h-5 w-5" />
                        </span>
                        <h3 className="mt-4 pf-display text-[20px] leading-snug">
                          {feat.title}
                        </h3>
                        <p className="mt-2 text-[14px] text-[hsl(var(--muted-foreground))] leading-relaxed">
                          {feat.desc}
                        </p>
                      </article>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </section>
        )}

      {/* Highlights */}
      {sections.highlights !== false &&
        Array.isArray(page.highlights) &&
        page.highlights.length > 0 && (
          <section
            data-testid={`app-page-${page.slug}-highlights`}
            className="pf-section bg-[#121316] text-[#FBFAF8] relative overflow-hidden"
          >
            <div
              className="pointer-events-none absolute inset-0 pf-charcoal-glow"
              aria-hidden="true"
            />
            <div
              className="pointer-events-none absolute inset-0 pf-noise"
              aria-hidden="true"
            />
            <div className="pf-container relative">
              <Reveal>
                <div className="flex items-center gap-2">
                  <span
                    className="pf-mono text-[11px] uppercase tracking-wider"
                    style={{ color: accent }}
                  >
                    {page.highlights_eyebrow || "Why we built it"}
                  </span>
                  <span className="h-px w-10 bg-white/15" />
                </div>
                <h2 className="mt-3 pf-display text-[32px] sm:text-[44px] leading-tight tracking-tight max-w-2xl">
                  {page.highlights_title || "A little app with a lot of heart."}
                </h2>
              </Reveal>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                {page.highlights.map((h, i) => {
                  const Icon = getIcon(h.icon || "Sparkles");
                  return (
                    <Reveal key={h.id || i} delay={0.04 * i}>
                      <div
                        data-testid={`app-page-${page.slug}-highlight-${i}`}
                        className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                      >
                        <span
                          className="inline-flex h-11 w-11 items-center justify-center rounded-xl"
                          style={{ backgroundColor: accent }}
                        >
                          <Icon className="h-5 w-5 text-white" />
                        </span>
                        <h3 className="mt-4 pf-display text-[20px] leading-snug text-white">
                          {h.title}
                        </h3>
                        <p className="mt-2 text-[14px] text-white/70 leading-relaxed">
                          {h.desc}
                        </p>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </section>
        )}

      {/* FAQ */}
      {sections.faq !== false &&
        Array.isArray(page.faq) &&
        page.faq.length > 0 && (
          <section
            data-testid={`app-page-${page.slug}-faq`}
            className="pf-section"
          >
            <div className="pf-container max-w-3xl">
              <Reveal>
                <div className="flex items-center gap-2">
                  <span
                    className="pf-mono text-[11px] uppercase tracking-wider"
                    style={{ color: accent }}
                  >
                    {page.faq_eyebrow || "FAQ"}
                  </span>
                  <span className="h-px w-10 bg-[hsl(var(--border))]" />
                </div>
                <h2 className="mt-3 pf-display text-[32px] sm:text-[44px] leading-tight tracking-tight">
                  {page.faq_title || "Good questions."}
                </h2>
              </Reveal>

              <Accordion type="single" collapsible className="mt-8 space-y-3">
                {page.faq.map((item, i) => (
                  <AccordionItem
                    key={item.id || i}
                    value={`faq-${i}`}
                    data-testid={`app-page-${page.slug}-faq-${i}`}
                    className="rounded-2xl border border-[hsl(var(--border))] bg-white px-5"
                  >
                    <AccordionTrigger className="text-left text-[16px] py-4 hover:no-underline">
                      <span className="flex items-center gap-3">
                        <ChevronDown className="h-4 w-4 text-[#F35A1F] shrink-0 [.group[data-state=open]_&]:rotate-180 transition-transform" />
                        {item.q}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-[14px] text-[hsl(var(--muted-foreground))] pb-4 leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

      {/* CTA */}
      {sections.cta !== false && (
        <section
          data-testid={`app-page-${page.slug}-cta`}
          className="pf-section"
        >
          <div className="pf-container">
            <div
              className="relative overflow-hidden rounded-3xl p-10 sm:p-14 text-white"
              style={{
                background: `linear-gradient(135deg, ${accent}, #D94A14)`,
              }}
            >
              <div
                className="pointer-events-none absolute inset-0 pf-noise opacity-20"
                aria-hidden="true"
              />
              <div className="relative max-w-2xl">
                {page.cta_eyebrow ? (
                  <p className="pf-mono text-[11px] uppercase tracking-wider text-white/80">
                    {page.cta_eyebrow}
                  </p>
                ) : null}
                <h2
                  data-testid={`app-page-${page.slug}-cta-title`}
                  className="mt-2 pf-display text-[28px] sm:text-[40px] leading-tight tracking-tight"
                >
                  {page.cta_title || "Ready to give it a go?"}
                </h2>
                {page.cta_subtitle ? (
                  <p className="mt-3 text-white/85 max-w-xl">
                    {page.cta_subtitle}
                  </p>
                ) : null}
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  {page.coming_soon || !hasAppStoreLink ? (
                    <Button
                      onClick={openNotify}
                      data-testid={`app-page-${page.slug}-cta-button`}
                      className="h-12 rounded-xl bg-white px-5 text-base font-semibold text-[#121316] hover:bg-white/90"
                    >
                      <Bell className="h-4 w-4 mr-2" />
                      {page.cta_button_label || "Notify Me"}
                    </Button>
                  ) : (
                    <a
                      href={page.app_store_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`app-page-${page.slug}-cta-button`}
                      className="inline-flex items-center gap-2 h-12 rounded-xl bg-white px-5 text-base font-semibold text-[#121316] hover:bg-white/90"
                    >
                      <Download className="h-4 w-4" />
                      {page.cta_button_label || "Get the App"}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer content={content?.footer} />

      <NotifyForm
        key={notifySession}
        open={notifyOpen}
        onOpenChange={setNotifyOpen}
        initialAppInterest={page.title}
        appOptions={[page.title]}
      />
    </div>
  );
}
